import { ID_PATTERN, NONE_VALUE, SCENARIO_TYPE_LABELS, SETUP_TYPE_LABELS } from "../../data-utils/kpiConfig"

// Dashboard filter state (spec §11.2, M11): the range and every filter live in the URL, so a
// refresh, a shared link, print and exports all keep them. Values are validated on read —
// anything unknown is dropped rather than sent (the server would 400 on it).

export const RANGE_OPTIONS = Object.freeze([3, 6, 12])
export const DEFAULT_RANGE_MONTHS = 6
// CTF reports spec §5/§6: Type = All · CTF · Training (guided) · Assessment. All three modes are
// sent to the KPI server; under "ctf" it scopes every metric to CTF runs and the dashboard also
// shows the CTF notice (readiness scores cover tabletop runs only).
export const RUNS_OPTIONS = Object.freeze([
  ["all", "All runs"],
  ["ctf", "CTF only"],
  ["guided", "Training only"],
  ["assessment", "Assessment only"],
])
const RUN_MODES = new Set(["assessment", "guided", "ctf"])

const LIST_PARAMS = Object.freeze({ scenarioType: "type", department: "dept", setupType: "setup" })
const ENUMS = Object.freeze({ scenarioType: Object.keys(SCENARIO_TYPE_LABELS), setupType: Object.keys(SETUP_TYPE_LABELS) })
const GROUP_PREFIX = "g."
// Request-schema limits (spec §7.2): department ≤ 20 values; groups ≤ 10 keys × 50 values.
// Enum lists (scenarioType, setupType) are bounded by their enum.
export const URL_LIMITS = Object.freeze({ department: 20, groupKeys: 10, groupValues: 50 })

// Free-form ids (departments, group values) must be `__none__` or a config id; enum lists
// must be one of their enum values. Anything else is dropped, never sent.
const isIdValue = (v) => v === NONE_VALUE || ID_PATTERN.test(v)
const listFrom = (raw, allowed = null, max = Infinity) =>
  [
    ...new Set(
      String(raw ?? "")
        .split(",")
        .filter((v) => (allowed ? allowed.includes(v) : isIdValue(v)))
    ),
  ].slice(0, max)

export const readDashboardParams = (searchParams) => {
  const range = Number(searchParams.get("range"))
  const rangeMonths = RANGE_OPTIONS.includes(range) ? range : DEFAULT_RANGE_MONTHS
  const filters = {}
  for (const [field, param] of Object.entries(LIST_PARAMS)) {
    const values = listFrom(searchParams.get(param), ENUMS[field] ?? null, field === "department" ? URL_LIMITS.department : Infinity).sort()
    if (values.length) filters[field] = values
  }
  // Group filters (g.<key>, e.g. g.businessUnit) were retired 2026-10: older links' g.* params are
  // ignored (and cleared by the clear helpers below).
  const runs = searchParams.get("runs")
  if (RUN_MODES.has(runs)) filters.mode = runs
  return { rangeMonths, filters }
}

export const patchDashboardParams = (prev, patch) => {
  const next = new URLSearchParams(prev)
  const setList = (param, values) => {
    if (Array.isArray(values) && values.length) next.set(param, values.join(","))
    else next.delete(param)
  }
  if ("rangeMonths" in patch) next.set("range", String(patch.rangeMonths))
  for (const [field, param] of Object.entries(LIST_PARAMS)) if (field in patch) setList(param, patch[field])
  if ("mode" in patch) {
    if (RUN_MODES.has(patch.mode)) next.set("runs", patch.mode)
    else next.delete("runs")
  }
  return next
}

export const clearDashboardFilters = (prev) => {
  const next = new URLSearchParams(prev)
  for (const param of [...Object.values(LIST_PARAMS), "runs"]) next.delete(param)
  for (const key of [...next.keys()]) if (key.startsWith(GROUP_PREFIX)) next.delete(key)
  return next
}

// A manager switching organization: departments and group values are per-org ids, so they
// no longer mean anything. Range, scenario type, setup and runs carry over.
export const clearOrgScopedFilters = (prev) => {
  const next = new URLSearchParams(prev)
  next.delete(LIST_PARAMS.department)
  for (const key of [...next.keys()]) if (key.startsWith(GROUP_PREFIX)) next.delete(key)
  return next
}

// Every active filter counts, Runs included — the same set the chips show ("Filters (3)" = 3 chips).
export const activeFilterCount = (filters) =>
  Object.keys(LIST_PARAMS).filter((f) => filters?.[f]?.length).length + (RUN_MODES.has(filters?.mode) ? 1 : 0)

// "CTF only" is sent as mode "ctf": the KPI server scopes every metric to CTF runs (tabletop
// outcome metrics come back empty with reason ctfRunsNotScored), so the dashboard never shows
// tabletop results under the CTF filter.
export const kpiRequestFilters = ({ rangeMonths, filters }) => ({ range: { months: rangeMonths }, ...filters })

const labelFrom = (list, id, fallback) => (list ?? []).find((x) => x.id === id)?.label ?? fallback

export const filterChips = ({ filters, dims }) => {
  const chips = []
  if (filters?.scenarioType?.length) {
    chips.push({
      id: "scenarioType",
      label: "Scenario type",
      value: filters.scenarioType.map((id) => SCENARIO_TYPE_LABELS[id] ?? id).join(", "),
      patch: { scenarioType: [] },
    })
  }
  if (filters?.department?.length) {
    const ordered = [...filters.department].sort((a, b) => (a === NONE_VALUE) - (b === NONE_VALUE))
    chips.push({
      id: "department",
      label: "Exercises involving",
      value: ordered.map((id) => labelFrom(dims?.department, id, id === NONE_VALUE ? "No department" : "Unknown")).join(", "),
      patch: { department: [] },
    })
  }
  if (filters?.setupType?.length) {
    chips.push({
      id: "setupType",
      label: "Setup",
      value: filters.setupType.map((id) => SETUP_TYPE_LABELS[id] ?? id).join(", "),
      patch: { setupType: [] },
    })
  }
  return chips
}

export const runsLabel = (mode) => (RUNS_OPTIONS.find(([value]) => value === (mode ?? "all")) ?? RUNS_OPTIONS[0])[1]

export const filterLine = ({ rangeMonths, filters, dims }) =>
  [`Last ${rangeMonths} months`, ...filterChips({ filters, dims }).map((c) => `${c.label}: ${c.value}`), `Runs: ${runsLabel(filters?.mode)}`].join(
    " · "
  )

export const kpiDashboardView = (response) => {
  if (!response) return null
  const results = Object.fromEntries((response.results ?? []).map((r) => [r.id, r]))
  const summary = results.summary ?? null
  return {
    metrics: summary?.value ?? null,
    warnings: summary?.warnings ?? [],
    source: summary?.source ?? null,
    dims: results.dims?.value ?? null,
    coverage: response.coverage ?? null,
    asOf: response.asOf ?? null,
    // People floor as the server answered it; null when not echoed (copy then stays neutral).
    k: typeof response.privacy?.minGroupSize === "number" ? response.privacy.minGroupSize : null,
    // Team-run floor as the server answered it; null when not echoed (copy then stays neutral).
    minTeamRuns: typeof response.privacy?.minTeamRuns === "number" ? response.privacy.minTeamRuns : null,
    individualResults: response.privacy?.individualResults ?? null,
    // KPI P2: per-lineage latest comparable pair, keyed by SCENARIO ID (the Most-run rows' key) —
    // never by title: two scenarios can share a title.
    runComparison: runComparisonByScenario(results.runComparison?.cells),
    results,
  }
}

// scenario id → the lineage cell's value. A lineage spans every scenario id it ran under; when two
// lineages claim one id (shouldn't happen), the first — the most-run — keeps it.
export const runComparisonByScenario = (cells) => {
  const out = new Map()
  for (const cell of cells ?? []) for (const id of cell?.value?.scenarioIds ?? []) if (!out.has(id)) out.set(id, cell.value)
  return out
}

// The Avg score delta badge: ▲ green only when it rose, ▼ red when it fell, "No change" in a neutral
// tone at 0 — never a green "▲ 0 pts". null when there's no honest delta.
export const avgDeltaBadge = (delta) => {
  if (typeof delta !== "number" || !Number.isFinite(delta)) return null
  if (delta > 0) return { text: `▲ ${delta} pts`, tone: "is-clear" }
  if (delta < 0) return { text: `▼ ${Math.abs(delta)} pts`, tone: "is-down" }
  return { text: "No change", tone: "is-neutral" }
}

// Plain-language notices for what the view's numbers don't include yet (summary.coverage, scoped to
// the range and filters, counted in exercises like the tiles). Nothing pending → no notice.
const exercisesWord = (n) => (n === 1 ? "exercise" : "exercises")
const stillProcessing = (n) => (n === 1 ? "1 is still being processed and appears shortly" : `${n} are still being processed and appear shortly`)
export const coverageNotices = (coverage) => {
  const out = []
  const scores = coverage?.scores
  if (scores?.pending > 0) {
    out.push(
      `Scores include ${scores.included} of ${scores.finished} finished ${exercisesWord(scores.finished)} in this view; ${stillProcessing(scores.pending)}.`
    )
  }
  const details = coverage?.details
  if (details?.pending > 0 || details?.failed > 0) {
    const parts = [`Department details include ${details.included} of ${details.finished} finished ${exercisesWord(details.finished)} in this view`]
    if (details.pending > 0) parts.push(stillProcessing(details.pending))
    if (details.failed > 0) parts.push(`${details.failed} couldn't be processed`)
    out.push(`${parts.join("; ")}.`)
  }
  return out
}

// "▲ 6 pts" / "▼ 4 pts" / "No change" for a lineage's latest comparable pair (Minor 15: a hidden
// cell says so, never "no comparable run").
export const comparablePairText = (value) => {
  if (!value) return "—"
  if (value.hidden) return "Hidden under this filter"
  const d = value.latestPair?.delta
  if (d == null) return "No comparable run"
  return d > 0 ? `▲ ${d} pts` : d < 0 ? `▼ ${Math.abs(d)} pts` : "No change"
}

// "Scenarios run": the server's distinct count over the whole view (the byScenario list is only the
// top 20). Older payloads without it fall back to the scenarios that have runs.
export const scenariosRunCount = (metrics) =>
  typeof metrics?.scenarios?.distinctCount === "number"
    ? metrics.scenarios.distinctCount
    : (metrics?.scenarios?.byScenario ?? []).filter((r) => r.count > 0).length

export const isEmptyForFilters = (metrics) => (metrics?.exercises?.total ?? 0) === 0 && (metrics?.readiness?.scoredSessions ?? 0) === 0

// The filters the server says it ANSWERED (response.filtersApplied), in the URL-state shape.
// Exports label themselves from this, so a file can never claim filters the numbers weren't
// computed under. Falls back to the requested state when the server didn't echo them.
export const answeredFilterState = ({ filtersApplied, fallback }) => {
  if (!filtersApplied || typeof filtersApplied !== "object") return fallback
  // eslint-disable-next-line no-unused-vars
  const { range, mode, groups, ...lists } = filtersApplied
  const filters = {}
  for (const field of Object.keys(LIST_PARAMS)) if (Array.isArray(lists[field]) && lists[field].length) filters[field] = [...lists[field]]
  if (RUN_MODES.has(mode)) filters.mode = mode
  return { rangeMonths: Number.isFinite(range?.months) ? range.months : fallback.rangeMonths, filters }
}

// Copy for a value the server hid, by its `reason` (spec §7.7). No reason → the people floor.
// Floors come from the response (privacy.minGroupSize / privacy.minTeamRuns), never a constant.
export const hiddenReasonCopy = ({ reason, k, minTeamRuns = null }) => {
  switch (reason) {
    case "belowMinRuns":
      return typeof minTeamRuns === "number" ? `Hidden: fewer than ${minTeamRuns} team runs` : "Hidden: too few team runs"
    case "complementary":
      return "Hidden: a smaller hidden group could otherwise be worked out from it"
    case "complementBelowMinGroupSize":
      return "Hidden: too few people fall outside this filter to protect their results."
    default:
      return typeof k === "number" ? `Hidden: fewer than ${k} people` : "Hidden: too few people"
  }
}

// Why part of a value is hidden, without the leading "Hidden: " (it's appended to "Part of this
// value is hidden: …" on screen and exported as the reason in the CSV). No reason → neutral copy.
export const PARTIAL_NO_REASON = "Part of this value is hidden to protect individuals."
export const partialReasonText = ({ reason, k, minTeamRuns = null }) =>
  reason ? hiddenReasonCopy({ reason, k, minTeamRuns }).replace(/^Hidden:\s*/, "") : null

// A month is hidden only when the server suppressed it AND it actually had runs (M3).
export const isHiddenMonth = (p) => p?.suppressed === true && p?.count > 0

export const PRACTICE_NOT_SCORED = "practiceRunsNotScored"

// Same ids as KPI_DASHBOARD_QUERY_IDS in data-utils/metricsUtils.js — not imported, because that
// module pulls in data.js and this one stays a pure, dependency-light helper.
const DEPT_RESULT_IDS = Object.freeze({ people: "deptPeople", runs: "deptRuns", owned: "deptOwned" })
const cellsById = (result) => new Map((result?.cells ?? []).map((c) => [c.key?.department, c]))
const outcome = (cell) => ({
  value: cell?.value ?? null,
  suppressed: cell?.suppressed === true,
  reason: cell?.reason ?? null,
  // Part of the value was hidden by a floor (spec §7.7 mixed cells / sub-cells); the rest is shown.
  partial: cell?.partial === true,
})

// The three department results must all be in the answer; a missing one is an error for the card,
// never "no department data" (an unknown is not an empty).
export const departmentResultsMissing = (results) => Object.values(DEPT_RESULT_IDS).some((id) => !results?.[id] || !Array.isArray(results[id].cells))

// Departments card rows (spec §11.2): People = participant.count, Exercises = the summary's
// exercises in view per department (summary.departments: the Exercises tile's unit, a group once;
// exercise.finishedRuns only for an older server), Owned = department.ownedActionRate (+ trend).
// `dims`: the filter dimension values — their department labels name a department that only the
// summary lists (no people/runs/owned cell), instead of showing its raw id.
export const departmentRows = ({ results, dims = null }) => {
  const people = cellsById(results?.[DEPT_RESULT_IDS.people])
  const viewDepartments = results?.summary?.value?.departments
  const runCells = cellsById(results?.[DEPT_RESULT_IDS.runs])
  // A department whose exercise count a privacy floor hides arrives without a number (suppressed, or
  // exercises null): it stays hidden — never shown as 0, never as the real count.
  const runs = Array.isArray(viewDepartments)
    ? new Map(
        viewDepartments.map((d) => [
          d.id,
          {
            value: d.suppressed === true || typeof d.exercises !== "number" ? null : d.exercises,
            suppressed: d.suppressed === true || typeof d.exercises !== "number",
            label: runCells.get(d.id)?.label,
          },
        ])
      )
    : runCells
  const dimLabels = new Map((dims?.department ?? []).map((d) => [d.id, d.label]))
  const owned = cellsById(results?.[DEPT_RESULT_IDS.owned])
  const ids = [...new Set([...people.keys(), ...runs.keys(), ...owned.keys()])].filter((id) => id != null)
  const rows = ids.map((id) => {
    const o = owned.get(id)
    const label =
      people.get(id)?.label?.department ??
      runs.get(id)?.label?.department ??
      o?.label?.department ??
      dimLabels.get(id) ??
      (id === NONE_VALUE ? "No department" : id)
    const peopleCount = people.get(id)?.value ?? 0
    const exercisesHidden = runs.get(id)?.suppressed === true
    const exercises = exercisesHidden ? null : (runs.get(id)?.value ?? 0)
    // A privacy floor hid the owned rate: belowMinRuns = facilitated team runs under the run floor;
    // every other floor reason counts as people. Practice-only runs aren't a floor — they're unscored.
    const floorHidden = o?.suppressed === true && o.reason !== PRACTICE_NOT_SCORED
    const hidden = floorHidden ? (o.reason === "belowMinRuns" ? "teamRuns" : "people") : null
    const ownedOutcome = outcome(o)
    return {
      id,
      label,
      people: peopleCount,
      exercises,
      exercisesHidden,
      owned: ownedOutcome,
      trend: typeof o?.trend === "number" ? o.trend : null,
      hidden,
      // The server's reason, so the copy names the floor that applied (complementary, complement guard…).
      hiddenReason: floorHidden ? (o.reason ?? null) : null,
      teamOnly: peopleCount === 0 && (exercises ?? 0) > 0,
      partial: ownedOutcome.partial,
    }
  })
  // "No department" sorts last, as in the filter chips.
  return rows
    .filter((r) => (r.exercises ?? 0) > 0 || r.people > 0 || r.exercisesHidden)
    .sort((a, b) => (a.id === NONE_VALUE) - (b.id === NONE_VALUE) || (b.exercises ?? -1) - (a.exercises ?? -1) || a.label.localeCompare(b.label))
}

// JSON export of the Departments card: the same rows the card renders, hidden values as null
// with the reason, never a number the card didn't show.
const exportRate = (row, cell) => ({
  value: row.hidden || cell?.suppressed ? null : (cell?.value ?? null),
  suppressed: row.hidden ? true : cell?.suppressed === true,
  reason: cell?.reason ?? null,
  partial: cell?.partial === true,
})
export const departmentRowsForJson = (rows) =>
  (rows ?? []).map((row) => ({
    id: row.id,
    label: row.label,
    people: row.people,
    exercises: row.exercises,
    ownedActionsCorrect: exportRate(row, row.owned),
    trend: row.hidden ? null : row.trend,
    hidden: row.hidden,
    hiddenReason: row.hiddenReason ?? null,
    teamOnly: row.teamOnly,
    partial: row.partial === true,
  }))
